import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Sale } from '../../shared/interfaces/sale';
import { Step } from '../../shared/interfaces/step';
import { Form } from '../../shared/interfaces/steps/form';
import { Video } from '../../shared/interfaces/steps/video';
import { Workflow } from '../../shared/interfaces/workflow';
import { CustomerFormsService } from './customer-forms.service';
import { Product } from '../../shared/interfaces/product';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Terms } from '../../shared/interfaces/steps/terms';
import { CustomerFormVariantType, VehicleFormVariantEnum } from '../../shared/interfaces/form';
import { VehicleSelectFormsService } from './vehicle-select-forms.service';

@Injectable({
    providedIn: 'root',
})
export class CustomerStepFormsService {

    public constructor(
        private readonly http: HttpClient,
        private readonly forms: FormBuilder,
        private readonly customerForms: CustomerFormsService,
        private readonly vehicleSelectForms: VehicleSelectFormsService,
    ) {
        //
    }

    public fromWorkflow(sale: Sale, workflow: Workflow): FormArray {
        const allSteps = (workflow.steps || []).map((s) => this.forStep(s, sale));
        return this.forms.array([...allSteps]);
    }

    public getCartProducts(sale: Sale): Observable<Product[]> {
        return this.http.get<Product[]>(`/api/sales/${sale.id}/cart`);
    }

    private forFormStep(step: Form, sale: Sale): FormGroup {
        switch(step.settings?.form) {
            case 'Customer Info':
                return this.customerForms.createCustomersFormGroup(
                    sale,
                    step.settings?.variant as CustomerFormVariantType,
                    step.settings,
                );
            case 'Vehicle Select':
                return this.vehicleSelectForms.createVehicleSelectFormGroup(
                    VehicleFormVariantEnum.DEFAULT,
                    { make_id: sale.make_id, model_id: sale.model_id, trim_id: sale.trim_id },
                );
            default:
                return this.forms.group({});
        }
    }

    private forStep(step: Step, sale: Sale): FormGroup {
        switch (step.slug) {
            case 'terms':
                return this.forTermsStep(sale, step as Terms);
            case 'video':
                return this.forVideoStep(sale, step as Video);
            case 'form':
                return this.forFormStep(step as Form, sale);
            default:
                return this.forms.group({});
        }
    }

    private forTermsStep(sale: Sale, step: Terms): FormGroup {
        const terms = sale.sale_properties?.filter(sp =>
            sp.step_id === step.id && sp.property === 'terms',
        )?.[0];
        return this.forms.group({
            terms: [!!terms?.value, Validators.requiredTrue],
        });
    }

    private forVideoStep(sale: Sale, step: Video): FormGroup {
        const watched = sale.sale_properties?.filter(sp =>
            sp.step_id === step.id && sp.property === 'watched',
        )?.[0];
        return this.forms.group({
            _watched: [!!watched?.value || !step.settings?.forced, Validators.requiredTrue],
        });
    }
}
